import React, { useMemo, useState } from 'react';
import { Field, useForm, useField } from 'react-final-form';
import i18next from 'i18next';
import { IconButton } from '@mui/material';
import { Keyboard, List } from '@mui/icons-material';
import { getCountries, getStates, getCities } from 'helpers/locations';
import SelectInput from 'components/inputs/SelectInput';
import { FormRowContainer } from 'styles/form';
import { requiredValidation } from 'helpers/validations';
import Input from 'components/inputs/Input';
import { FieldWrapper, InputContainer } from './styles';

import './i18n';

interface Props {
  path: string;
  values: any;
}

const OnChange = ({
  name,
  onChange,
}: {
  name: string;
  onChange: (value: any, previous?: any) => void;
}) => {
  const {
    input: { value },
  } = useField(name, { subscription: { value: true } });
  const [prev, setPrev] = React.useState(value);

  React.useEffect(() => {
    if (value !== prev) {
      const oldPrev = prev;
      setPrev(value);
      onChange(value, oldPrev);
    }
  }, [value, prev, onChange]);

  return null;
};

const LocationSelector: React.FC<Props> = ({ path, values }) => {
  const form = useForm();

  const [manualProvince, setManualProvince] = useState(false);
  const [manualLocality, setManualLocality] = useState(false);

  const currentPathValues = useMemo(() => {
    return path.split('.').reduce((obj, key) => obj?.[key], values);
  }, [path, values]);

  const selectedCountry = currentPathValues?.country;
  const selectedState = currentPathValues?.province;
  const currentLocality = currentPathValues?.locality;

  const countriesOptions = useMemo(() => getCountries(), []);

  const statesOptions = useMemo(() => {
    if (!selectedCountry) return [];
    const countryObj = countriesOptions.find(
      (c) => c.label === selectedCountry || c.value === selectedCountry,
    );
    const code = countryObj ? countryObj.value : selectedCountry;
    return (getStates(String(code).toUpperCase()) as any[]).map((s) => ({
      label: s.name,
      value: s.isoCode,
    }));
  }, [selectedCountry, countriesOptions]);

  const citiesOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const countryObj = countriesOptions.find(
      (c) => c.label === selectedCountry || c.value === selectedCountry,
    );
    const stateObj = statesOptions.find(
      (s) => s.label === selectedState || s.value === selectedState,
    );
    const cCode = countryObj ? countryObj.value : selectedCountry;
    const sCode = stateObj ? stateObj.value : selectedState;
    const cities = getCities(String(cCode).toUpperCase(), String(sCode).toUpperCase());
    return (cities as any[]).map((c) => ({ label: c.name, value: c.name }));
  }, [selectedCountry, selectedState, countriesOptions, statesOptions]);

  const countryExists = useMemo(() => {
    if (!selectedCountry) return false;
    return countriesOptions.some(
      (c) =>
        String(c.value).toUpperCase() === String(selectedCountry).toUpperCase() ||
        String(c.label).toUpperCase() === String(selectedCountry).toUpperCase(),
    );
  }, [selectedCountry, countriesOptions]);

  const stateExists = useMemo(() => {
    if (!selectedState) return false;
    return statesOptions.some(
      (s) =>
        String(s.value).toUpperCase() === String(selectedState).toUpperCase() ||
        String(s.label).toUpperCase() === String(selectedState).toUpperCase(),
    );
  }, [selectedState, statesOptions]);

  const hasCities = citiesOptions.length > 0;
  const isPlainTextInput = useMemo(() => {
    return !!currentLocality && (!countryExists || !stateExists || !hasCities);
  }, [currentLocality, countryExists, stateExists, hasCities]);
  const locationRequired = !currentLocality ? requiredValidation : undefined;

  return (
    <>
      <FormRowContainer>
        {/* PAÍS */}
        <FieldWrapper>
          <InputContainer>
            <Field name={`${path}.country`} validate={locationRequired}>
              {({ input, meta }) => {
                const option = countriesOptions.find(
                  (c) => c.label === input.value || c.value === input.value,
                );

                if (input.value && !countryExists) {
                  return (
                    <Input
                      input={input}
                      meta={meta}
                      label={i18next.t<string>('LOCATION_SELECTOR:COUNTRY')}
                    />
                  );
                }

                return (
                  <SelectInput
                    input={{
                      ...input,
                      value: option ? option.value : input.value,
                      onChange: (v: any) => {
                        const selected = countriesOptions.find((c) => c.value === v);
                        input.onChange(selected ? selected.label : v);
                      },
                    }}
                    meta={meta}
                    label={i18next.t<string>('LOCATION_SELECTOR:COUNTRY')}
                    options={countriesOptions}
                  />
                );
              }}
            </Field>
          </InputContainer>
        </FieldWrapper>

        {/* PROVINCIA */}
        <FieldWrapper>
          <InputContainer>
            <Field name={`${path}.province`} validate={locationRequired}>
              {({ input, meta }) => {
                const option = statesOptions.find(
                  (s) => s.label === input.value || s.value === input.value,
                );
                if (manualProvince || (input.value && !stateExists)) {
                  return (
                    <Input
                      input={input}
                      meta={meta}
                      label={i18next.t<string>('LOCATION_SELECTOR:PROVINCE')}
                    />
                  );
                }
                return (
                  <SelectInput
                    input={{
                      ...input,
                      value: option ? option.value : input.value,
                      onChange: (v: any) => {
                        const selected = statesOptions.find((s) => s.value === v);
                        input.onChange(selected ? selected.label : v);
                      },
                    }}
                    meta={meta}
                    label={i18next.t('LOCATION_SELECTOR:PROVINCE')}
                    options={statesOptions}
                    disabled={!selectedCountry}
                  />
                );
              }}
            </Field>
          </InputContainer>
          <IconButton
            onClick={() => {
              if (manualProvince) form.change(`${path}.province`, undefined);
              setManualProvince(!manualProvince);
            }}
            sx={{ mb: 2 }}
            disabled={!selectedCountry && !manualProvince}
          >
            {manualProvince ? <List /> : <Keyboard />}
          </IconButton>
        </FieldWrapper>

        {/* LOCALIDAD */}
        <FieldWrapper>
          <InputContainer>
            <Field
              name={`${path}.locality`}
              validate={
                manualLocality || isPlainTextInput || hasCities ? requiredValidation : undefined
              }
            >
              {({ input, meta }) => {
                const cityExists = citiesOptions.some(
                  (c) => c.value === input.value || c.label === input.value,
                );
                if (
                  manualLocality ||
                  isPlainTextInput ||
                  !hasCities ||
                  (input.value && !cityExists)
                ) {
                  return (
                    <Input
                      input={input}
                      meta={meta}
                      label={i18next.t('LOCATION_SELECTOR:LOCALITY')}
                    />
                  );
                }
                return (
                  <SelectInput
                    input={input}
                    meta={meta}
                    label={i18next.t('LOCATION_SELECTOR:LOCALITY')}
                    options={citiesOptions}
                    disabled={!selectedState}
                  />
                );
              }}
            </Field>
          </InputContainer>
          <IconButton
            onClick={() => {
              if (manualLocality) form.change(`${path}.locality`, undefined);
              setManualLocality(!manualLocality);
            }}
            sx={{ mb: 2 }}
            disabled={!selectedState && !manualLocality}
          >
            {manualLocality || isPlainTextInput || !hasCities ? <List /> : <Keyboard />}
          </IconButton>
        </FieldWrapper>
      </FormRowContainer>

      <OnChange
        name={`${path}.country`}
        onChange={(value: any, previous: any) => {
          const isManualChange = previous !== undefined && previous !== null && value !== previous;
          if (isManualChange && countryExists) {
            form.change(`${path}.province`, undefined);
            form.change(`${path}.locality`, undefined);
          }
        }}
      />

      <OnChange
        name={`${path}.province`}
        onChange={(value: any, previous: any) => {
          const isManualChange = previous !== undefined && previous !== null && value !== previous;
          if (isManualChange && stateExists) {
            form.change(`${path}.locality`, undefined);
          }
        }}
      />
    </>
  );
};
export default LocationSelector;
